import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import SidebarNavigation from './components/SidebarNavigation';
import TermsOfServiceContent from './components/TermsOfServiceContent';
import PrivacyPolicyContent from './components/PrivacyPolicyContent';
import CookieConsentBanner from './components/CookieConsentBanner';
import SearchFunctionality from './components/SearchFunctionality';

const LegalTermsPrivacyPage = () => {
  const [activeSection, setActiveSection] = useState('terms');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCookieBanner, setShowCookieBanner] = useState(true);

  // Handle section navigation
  const handleSectionChange = (section) => {
    setActiveSection(section);
    // Smooth scroll to top of content
    document.getElementById('main-content')?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  };

  // Handle search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  return (
    <>
      <Helmet>
        <title>Legal Terms & Privacy - Intime ATS | Complete Legal Documentation</title>
        <meta name="description" content="Read Intime's comprehensive legal documentation including Terms of Service, Privacy Policy, and Cookie Policy. Navigate easily with our organized legal hub." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://intime.com/legal-terms-privacy" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="border-b border-gray-200 bg-white sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-white" />
                </div>
                <span className="text-2xl font-bold text-gray-900">Intime</span>
              </Link>
              
              <nav className="flex items-center space-x-6">
                <Link to="/" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Home
                </Link>
                <Link to="/pricing" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Pricing
                </Link>
                <Link to="/careers" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Careers
                </Link>
                <Link to="/login" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Sign In
                </Link>
              </nav>
            </div>
          </div>
        </header>

        {/* Cookie Consent Banner */}
        {showCookieBanner && (
          <CookieConsentBanner onClose={() => setShowCookieBanner(false)} />
        )}

        {/* Main Content Layout */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Sidebar Navigation - Desktop */}
            <div className="hidden lg:block lg:w-80 flex-shrink-0">
              <div className="sticky top-24">
                <SidebarNavigation 
                  activeSection={activeSection}
                  onSectionChange={handleSectionChange}
                />
                
                {/* Search Functionality */}
                <div className="mt-6">
                  <SearchFunctionality 
                    searchQuery={searchQuery}
                    onSearch={handleSearch}
                  />
                </div>
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="lg:hidden">
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Legal Documents
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleSectionChange('terms')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeSection === 'terms' ?'bg-blue-600 text-white' :'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Terms of Service
                  </button>
                  <button
                    onClick={() => handleSectionChange('privacy')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeSection === 'privacy' ?'bg-blue-600 text-white' :'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Privacy Policy
                  </button>
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0">
              <div id="main-content" className="bg-white">
                
                {/* Page Header */}
                <div className="mb-8">
                  <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                    Legal Terms & Privacy
                  </h1>
                  <p className="text-xl text-gray-600 mb-6">
                    Comprehensive legal documentation for Intime's applicant tracking system
                  </p>
                  
                  {/* Document Navigation Indicator */}
                  <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
                    <Icon name="FileText" size={16} />
                    <span>
                      Currently viewing: {activeSection === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
                    </span>
                    <span>•</span>
                    <span>Last updated: December 17, 2024</span>
                  </div>
                </div>

                {/* Document Content */}
                <div className="prose prose-lg prose-gray max-w-none">
                  {activeSection === 'terms' ? (
                    <TermsOfServiceContent searchQuery={searchQuery} />
                  ) : (
                    <PrivacyPolicyContent searchQuery={searchQuery} />
                  )}
                </div>

                {/* Document Actions */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center space-x-4">
                      <button 
                        onClick={() => window.print()}
                        className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon name="Printer" size={16} />
                        <span>Print Document</span>
                      </button>
                      
                      <button 
                        onClick={() => {
                          const content = document.getElementById('main-content')?.textContent || '';
                          navigator.clipboard?.writeText(content);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon name="Copy" size={16} />
                        <span>Copy Text</span>
                      </button>
                    </div>
                    
                    <div className="text-sm text-gray-500">
                      <p>Document version: v2.1 • Effective: December 17, 2024</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-8">
                  <h2 className="text-2xl font-bold text-blue-900 mb-6">
                    Questions About Our Legal Documents?
                  </h2>
                  <p className="text-blue-800 mb-4">
                    If you have any questions about our Terms of Service, Privacy Policy, or data practices, please contact our legal team:
                  </p>
                  <div className="grid md:grid-cols-2 gap-6 text-blue-800">
                    <div>
                      <p className="font-semibold mb-2">Legal Inquiries</p>
                      <p><strong>Email:</strong> legal@intime.com</p>
                      <p><strong>Phone:</strong> +1 (555) 123-4567</p>
                    </div>
                    <div>
                      <p className="font-semibold mb-2">Privacy Concerns</p>
                      <p><strong>Email:</strong> privacy@intime.com</p>
                      <p><strong>Response Time:</strong> Within 48 hours</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <Link to="/" className="flex items-center space-x-2 mb-4 md:mb-0">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-white" />
                </div>
                <span className="text-2xl font-bold">Intime</span>
              </Link>
              
              <div className="flex items-center space-x-6 text-sm text-gray-400">
                <Link to="/legal/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/legal/terms" className="hover:text-white transition-colors">
                  Terms of Service
                </Link>
                <Link to="/legal-terms-privacy" className="text-white">
                  Legal Hub
                </Link>
                <span>&copy; 2024 Intime. All rights reserved.</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LegalTermsPrivacyPage;